/**
 * MLReadinessMetricDrawer Component
 * ============================================================================
 * Right-side drawer showing detailed information about an ML Readiness metric.
 * Displays score, findings, educational content, sub-metrics, citations,
 * and recommendations.
 *
 * Pattern: Follows the DataQualityCategoryDrawer pattern.
 *
 * Related Files:
 * - client/types/mlReadinessMetric.ts (type definitions)
 * - client/components/mlReadiness/useMLMetricDrawer.ts (state hook)
 * - client/components/dataQuality/DataQualityCategoryDrawer.tsx (pattern source)
 *
 * @version 1.0.0
 * @created 2026-01-29
 * ============================================================================
 */

import React, { useRef, useEffect, useState } from 'react';
import type {
  MLReadinessMetricDetail,
  MLMetricFinding,
  MLSubMetricValue,
  MLMetricRecommendation,
  RegulatoryMLCitation,
} from '../../types/mlReadinessMetric';
import {
  getRatingClass,
  getRatingLabel,
  getFindingClass,
  getFindingIcon,
  getSubMetricStatusClass,
  getPriorityClass,
} from '../../types/mlReadinessMetric';
import { InternalCitationCard } from '../governance/InternalCitationCard';
import './ml-readiness-metric-drawer.css';

// ============================================================================
// Types
// ============================================================================

export interface MLReadinessMetricDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** The metric detail to display */
  metricDetail: MLReadinessMetricDetail | null;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
}

// ============================================================================
// Component
// ============================================================================

export const MLReadinessMetricDrawer: React.FC<MLReadinessMetricDrawerProps> = ({
  isOpen,
  onClose,
  metricDetail,
  loading = false,
  error = null,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showAllCitations, setShowAllCitations] = useState(false);
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setShowAllCitations(false);
      setShowAllRecommendations(false);
    }
  }, [isOpen]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderFinding = (finding: MLMetricFinding, index: number) => (
    <li key={index} className={`ml-metric__finding ${getFindingClass(finding.type)}`}>
      <span className="ml-metric__finding-icon">{getFindingIcon(finding.type)}</span>
      <div className="ml-metric__finding-content">
        <span className="ml-metric__finding-message">{finding.message}</span>
        {finding.value !== undefined && (
          <span className="ml-metric__finding-value">({finding.value})</span>
        )}
        {finding.columns && finding.columns.length > 0 && (
          <span className="ml-metric__finding-columns">
            Columns: {finding.columns.slice(0, 3).join(', ')}
            {finding.columns.length > 3 && '...'}
          </span>
        )}
        {finding.details && (
          <span className="ml-metric__finding-details">{finding.details}</span>
        )}
      </div>
    </li>
  );

  const renderSubMetric = (metric: MLSubMetricValue, index: number) => (
    <div key={index} className={`ml-metric__submetric ${getSubMetricStatusClass(metric.status)}`}>
      <div className="ml-metric__submetric-header">
        <span className="ml-metric__submetric-name">{metric.name}</span>
        <span className="ml-metric__submetric-value">
          {metric.value !== null ? (
            <>
              {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
              {metric.unit && <span className="ml-metric__submetric-unit">{metric.unit}</span>}
            </>
          ) : (
            'N/A'
          )}
        </span>
      </div>
      {metric.details && (
        <p className="ml-metric__submetric-details">{metric.details}</p>
      )}
    </div>
  );

  const renderCitation = (citation: RegulatoryMLCitation, index: number) => (
    <div key={index} className="ml-metric__citation">
      <div className="ml-metric__citation-header">
        <span className="ml-metric__citation-framework">[{citation.framework}]</span>
        <span className="ml-metric__citation-document">{citation.document}</span>
      </div>
      <p className="ml-metric__citation-section">{citation.section}</p>
      <blockquote className="ml-metric__citation-text">
        "{citation.text}"
      </blockquote>
      {citation.url && (
        <a
          href={citation.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-metric__citation-link"
        >
          View Source Document
        </a>
      )}
    </div>
  );

  const renderRecommendation = (rec: MLMetricRecommendation, index: number) => (
    <div key={index} className={`ml-metric__recommendation ${getPriorityClass(rec.priority)}`}>
      <div className="ml-metric__recommendation-header">
        <span className="ml-metric__recommendation-priority">{rec.priority}</span>
        <span className="ml-metric__recommendation-title">{rec.title}</span>
      </div>
      <p className="ml-metric__recommendation-description">{rec.description}</p>
      {rec.impactEstimate !== undefined && (
        <span className="ml-metric__recommendation-impact">
          Impact: {rec.impactEstimate}/10
        </span>
      )}
    </div>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen) return null;

  const citations = metricDetail?.citations || [];
  // DC-1670: framework (regulatory) citations first, then the customer's own
  // governance under a "Your governance" subgroup. Collapse governs the
  // framework set only; internal citations stay visible.
  const frameworkCitations = citations.filter(c => c.source !== 'internal');
  const internalCitations = citations.filter(c => c.source === 'internal');
  const visibleCitations = showAllCitations ? frameworkCitations : frameworkCitations.slice(0, 2);
  const hasMoreCitations = frameworkCitations.length > 2;

  const recommendations = metricDetail?.recommendations || [];
  const visibleRecommendations = showAllRecommendations
    ? recommendations
    : recommendations.slice(0, 3);
  const hasMoreRecommendations = recommendations.length > 3;

  return (
    <>
      {/* Overlay */}
      <div
        className="ml-metric__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="ml-metric__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ml-metric-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="ml-metric__header">
          <div className="ml-metric__header-content">
            <h2 id="ml-metric-title" className="ml-metric__title">
              ML Readiness Metric
            </h2>
            {metricDetail && (
              <span className="ml-metric__name">
                <span className="ml-metric__icon">{metricDetail.icon}</span>
                {metricDetail.name}
              </span>
            )}
          </div>
          <button
            type="button"
            className="ml-metric__close-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="ml-metric__content">
          {loading ? (
            <div className="ml-metric__loading">
              <div className="ml-metric__loading-spinner" />
              <p>Loading metric details...</p>
            </div>
          ) : error ? (
            <div className="ml-metric__error" role="alert">
              <p>{error}</p>
            </div>
          ) : metricDetail ? (
            <>
              {/* Assessment Result Section */}
              <section className="ml-metric__section">
                <h3 className="ml-metric__section-title">Assessment Result</h3>
                <div className="ml-metric__result">
                  <div className="ml-metric__score-row">
                    <div className="ml-metric__score">
                      <span className="ml-metric__score-value">{metricDetail.score}%</span>
                      <span className="ml-metric__score-label">Score</span>
                    </div>
                    <span className={`ml-metric__status ${getRatingClass(metricDetail.rating)}`}>
                      {getRatingLabel(metricDetail.rating)}
                    </span>
                  </div>
                  <p className="ml-metric__result-summary">{metricDetail.assessmentSummary}</p>
                </div>
              </section>

              {/* Key Findings Section */}
              {metricDetail.findings.length > 0 && (
                <section className="ml-metric__section">
                  <h3 className="ml-metric__section-title">
                    Key Findings ({metricDetail.findings.length})
                  </h3>
                  <ul className="ml-metric__findings-list">
                    {metricDetail.findings.map((finding, i) => renderFinding(finding, i))}
                  </ul>
                </section>
              )}

              {/* What This Measures Section */}
              <section className="ml-metric__section">
                <h3 className="ml-metric__section-title">What This Measures</h3>
                <div className="ml-metric__definition">
                  <h4 className="ml-metric__definition-name">{metricDetail.name}</h4>
                  <p className="ml-metric__definition-text">
                    {metricDetail.whatItMeasures}
                  </p>
                </div>

                <div className="ml-metric__rationale">
                  <h4>Why This Matters</h4>
                  <p>{metricDetail.whyItMatters}</p>
                </div>

                {/* Score Thresholds */}
                <div className="ml-metric__thresholds">
                  <h4>Score Thresholds</h4>
                  <div className="ml-metric__thresholds-grid">
                    <div className="ml-metric__threshold ml-metric__threshold--excellent">
                      <span className="ml-metric__threshold-label">Excellent</span>
                      <span className="ml-metric__threshold-value">
                        ≥{metricDetail.thresholds.excellent}%
                      </span>
                    </div>
                    <div className="ml-metric__threshold ml-metric__threshold--good">
                      <span className="ml-metric__threshold-label">Good</span>
                      <span className="ml-metric__threshold-value">
                        ≥{metricDetail.thresholds.good}%
                      </span>
                    </div>
                    <div className="ml-metric__threshold ml-metric__threshold--poor">
                      <span className="ml-metric__threshold-label">Poor</span>
                      <span className="ml-metric__threshold-value">
                        ≥{metricDetail.thresholds.poor}%
                      </span>
                    </div>
                    <div className="ml-metric__threshold ml-metric__threshold--critical">
                      <span className="ml-metric__threshold-label">Critical</span>
                      <span className="ml-metric__threshold-value">
                        &lt;{metricDetail.thresholds.poor}%
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Sub-Metrics Breakdown Section */}
              {metricDetail.subMetrics.length > 0 && (
                <section className="ml-metric__section">
                  <h3 className="ml-metric__section-title">Sub-Metrics Breakdown</h3>
                  <div className="ml-metric__submetrics">
                    {metricDetail.subMetrics.map((metric, i) => renderSubMetric(metric, i))}
                  </div>
                </section>
              )}

              {/* Regulatory Citations Section */}
              {citations.length > 0 && (
                <section className="ml-metric__section">
                  <h3 className="ml-metric__section-title">
                    Regulatory Citations ({citations.length})
                  </h3>
                  <div className="ml-metric__citations">
                    {visibleCitations.map((citation, i) => renderCitation(citation, i))}
                  </div>
                  {hasMoreCitations && (
                    <button
                      className="ml-metric__show-more"
                      onClick={() => setShowAllCitations(!showAllCitations)}
                    >
                      {showAllCitations
                        ? 'Show fewer citations'
                        : `Show ${frameworkCitations.length - 2} more citation${frameworkCitations.length - 2 !== 1 ? 's' : ''}`}
                    </button>
                  )}
                  {internalCitations.length > 0 && (
                    <>
                      <p className="ic-subgroup">Your governance</p>
                      <div className="ml-metric__citations">
                        {internalCitations.map((citation, i) => (
                          <InternalCitationCard key={`internal-${i}`} citation={citation} />
                        ))}
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* Recommendations Section */}
              {recommendations.length > 0 && (
                <section className="ml-metric__section">
                  <h3 className="ml-metric__section-title">Recommendations</h3>
                  <div className="ml-metric__recommendations">
                    {visibleRecommendations.map((rec, i) => renderRecommendation(rec, i))}
                  </div>
                  {hasMoreRecommendations && (
                    <button
                      className="ml-metric__show-more"
                      onClick={() => setShowAllRecommendations(!showAllRecommendations)}
                    >
                      {showAllRecommendations
                        ? 'Show fewer recommendations'
                        : `Show ${recommendations.length - 3} more`}
                    </button>
                  )}
                </section>
              )}

              {/* Not Analyzed Notice */}
              {!metricDetail.wasAnalyzed && metricDetail.notAnalyzedReason && (
                <section className="ml-metric__section ml-metric__section--notice">
                  <div className="ml-metric__notice">
                    <span className="ml-metric__notice-icon">ℹ️</span>
                    <p>{metricDetail.notAnalyzedReason}</p>
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="ml-metric__empty">
              <p>No metric selected</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ml-metric__footer">
          <a
            href="/guides/ml-readiness"
            className="ml-metric__footer-link"
          >
            View ML Readiness Guide
          </a>
          <button
            type="button"
            className="ml-metric__footer-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default MLReadinessMetricDrawer;
